<script setup lang="ts">
import { ref, defineProps, defineEmits, inject, toRef, watch } from "vue"

const props = defineProps<{
  label: string
  tags: string[]
  trackId: string
}>()

const emit = defineEmits<{
  (e: "update", tags: string[]): void
}>()

const isAdmin = inject("isAdmin")
const isEditing = ref(false)
const localTags = ref<string[]>([...props.tags])
const newTag = ref("")
const inputRef = ref<HTMLInputElement | null>(null)

watch(
  () => props.tags,
  newTags => {
    localTags.value = [...newTags]
  }
)

function toggleEdit() {
  isEditing.value = !isEditing.value
  if (isEditing.value) {
    setTimeout(() => inputRef.value?.focus(), 0)
  } else {
    emit("update", localTags.value)
  }
}

function addTag() {
  const tag = newTag.value.trim()
  if (tag && !localTags.value.includes(tag)) {
    localTags.value.push(tag)
    newTag.value = ""
  }
}

function removeTag(index: number) {
  localTags.value.splice(index, 1)
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Enter") {
    e.preventDefault()
    addTag()
  } else if (e.key === "Escape") {
    isEditing.value = false
    localTags.value = [...props.tags]
  }
}

function onBlur() {
  if (newTag.value.trim()) {
    addTag()
  }
  setTimeout(() => {
    if (!inputRef.value?.matches(":focus")) {
      isEditing.value = false
      emit("update", localTags.value)
    }
  }, 100)
}
</script>

<template>
  <div class="border dark:border-zinc-700 rounded shadow-sm py-2 px-3">
    <div class="flex justify-between items-center mb-2">
      <span class="text-zinc-500">{{ label }}</span>
      <button v-if="isAdmin" class="text-sm text-zinc-400" @click="toggleEdit">
        <i class="bi bi-pencil"></i>
      </button>
    </div>

    <div v-if="isEditing" class="space-y-2">
      <div class="flex flex-wrap gap-1">
        <span
          v-for="(tag, index) in localTags"
          :key="tag"
          class="inline-flex items-center px-2 py-1 text-xs rounded bg-zinc-200 dark:bg-zinc-700 text-black dark:text-white"
        >
          {{ tag }}
          <button class="ml-1 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300" @click="removeTag(index)">
            <i class="bi bi-x"></i>
          </button>
        </span>
      </div>
      <input
        ref="inputRef"
        v-model="newTag"
        type="text"
        class="text-input h-8 w-full text-sm"
        placeholder="Add tag..."
        @keydown="onKeydown"
        @blur="onBlur"
      />
    </div>

    <div v-else class="min-h-[2rem]">
      <div v-if="localTags.length > 0" class="flex flex-wrap gap-1">
        <span
          v-for="tag in localTags"
          :key="tag"
          class="inline-flex items-center px-2 py-1 text-xs rounded bg-zinc-100 dark:bg-zinc-800 text-black dark:text-white"
        >
          {{ tag }}
        </span>
      </div>
      <span v-else class="text-zinc-400">—</span>
    </div>
  </div>
</template>
